import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { buildVendorParam, extractVendorId } from "../utils/vendorSlug";
import { productPathUnder, shopPath } from "../utils/seoPaths";
import CrawlLink from "../components/CrawlLink";
import { vendorView } from "../utils/analytics";
import { SEOHead } from "../components/SEO/SEOHead";
import {
  buildVendorSeoProps,
  buildVendorShareText,
  buildProductShareText,
  withVendorCacheBuster,
} from "../components/SEO/vendorSeo";
import styled, { keyframes } from "styled-components";
import usePublishedHeight from "../hooks/usePublishedHeight";
import {
  SECTIONS_H_VAR,
  stickyUnderNav,
  sectionScrollMargin,
  heightUnderStack,
  scrollToSection,
} from "../utils/stickyStack";
import { fetchVendorDetails } from "../redux/wholesaleRetailVendorSlice";
import {
  setCurrentVendor,
  mergeVendorCarts,
} from "../redux/retailerWholesalerCartSlice";
import Navbar from "../components/Navbar";
import { AddShoppingCart, CheckCircle, Storefront } from "@material-ui/icons";
import {
  Tooltip,
  Badge,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from "@mui/material";
import { toast } from "react-toastify";
import LinkIcon from "@mui/icons-material/Link";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ShareIcon from "@mui/icons-material/Share";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
// import IosShareIcon from "@mui/icons-material/IosShare";
import vendorBackgroundBanner from "../assets/vendorBackgroundBanner.png";
import RetailerWholesalerCart from "../components/RetailerWholesalerCart";
import RetailerWholesalerProductModal from "./RetailerWholesalerProductModal";
import Reviews from "../components/Reviews/Reviews";
import Stars from "../components/shop/Stars";
import { getVendorRatingSummary } from "../api/reviewsApi";
import VendorWhatsAppFloat from "../components/VendorWhatsAppFloat";
import ProductShareModal from "../components/ProductShare/ProductShareModal";
import { formatCurrency, SITE_ORIGIN } from "../config/market";
import useCoverage from "../redux/useCoverage";
import useVendorAvailability from "../hooks/useRestaurantAvailability";
import CoverageBanner from "../components/CoverageBanner";
import RestaurantAvailabilityBanner from "../components/RestaurantAvailabilityBanner";
import LocationPrompt from "../components/LocationPrompt";
import useCatalogUpdates from "../hooks/useCatalogUpdates";
import DealTag from "../components/DealTag";
import PriceCut from "../components/PriceCut";
import { hasSalePrice } from "../utils/vendorDiscounts";
import {
  cardFooterRow,
  cardPriceRow,
  cardPriceOwnsItsRow,
  cardActionsRow,
} from "../components/productCardFooter";
import VendorTagline from "../components/VendorTagline";

const VendorShopPage = () => {
  const { vendorId: vendorParam } = useParams();
  const vendorId = extractVendorId(vendorParam);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const vendor = useSelector((state) => state.wholesaleRetailVendor.vendor);
  const cart = useSelector(
    (state) => state.retailerWholesalerCart.carts[vendorId] || {},
  );
  const cartCount = Object.values(cart).reduce(
    (total, item) => total + item.quantity,
    0,
  );
  const status = useSelector((state) => state.wholesaleRetailVendor.isFetching);
  const error = useSelector((state) => state.wholesaleRetailVendor.error);
  // Authoritative delivery coverage for THIS vendor vs the customer's set
  // location (backend GET /api/coverage — same decision as the order-create
  // gate). Powers the shop-entry banner + the add-to-cart hard-block so the
  // customer learns "can't deliver here" now, not at the final Confirm step.
  // vendorType MUST stay "retailer" to match RetailerWholesalerCart.js and the
  // backend coverage-key convention for this vendor type.
  const coverage = useCoverage({ vendorId, vendorType: "retailer" });
  // Business hours. These verticals had no hours concept until the shared
  // availability service shipped; without this the vendor can mark themselves
  // closed in the dashboard and shoppers would never know.
  const availability = useVendorAvailability({
    vendorId: vendorId,
    vendorType: "retailer",
  });
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  const sectionsNavRef = useRef(null);
  /* The strip's rendered height, for the scroll margins and the inner
     scroller's height (utils/stickyStack). */
  usePublishedHeight(sectionsNavRef, SECTIONS_H_VAR);
  const [activeSection, setActiveSection] = useState(null);
  const [vendorRating, setVendorRating] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  // Visual share poster modal (branded poster preview + share actions).
  const [shareProduct, setShareProduct] = useState(null);
  const [shareProductUrl, setShareProductUrl] = useState("");

  const handleImageLoaded = useCallback((productId) => {
    setLoadedImages((prev) => ({ ...prev, [productId]: true }));
  }, []);

  // Durable, URL-independent vendor attribution (GA4 vendor_view).
  const vendorViewedRef = useRef(null);
  useEffect(() => {
    const vid = vendor?._id;
    if (!vid || vendorViewedRef.current === String(vid)) return;
    vendorViewedRef.current = String(vid);
    vendorView({ vendorId: vid, vendorType: vendor?.vendorType || "retailer" });
  }, [vendor?._id, vendor?.vendorType]);

  useEffect(() => {
    dispatch(fetchVendorDetails(vendorId));
    dispatch(setCurrentVendor(vendorId));
  }, [dispatch, vendorId]);

  // Live per-item visibility: when THIS vendor (or staff QC) deactivates or
  // reactivates a product, the storefront re-pulls the sections tree so the
  // item disappears/reappears without a reload. The page already renders only
  // `product.enabled` items, so a refetch is all that's needed — the backend
  // stays the source of truth. (catalog:public `product:statusChanged`,
  // playbook 10.)
  const vendorOwnerRef = useRef(null);
  useEffect(() => {
    vendorOwnerRef.current = vendor?.owner ? String(vendor.owner) : null;
  }, [vendor?.owner]);

  useCatalogUpdates(
    {
      "product:statusChanged": (payload) => {
        const mine =
          String(payload.vendorId || "") === String(vendorId) ||
          (vendorOwnerRef.current &&
            String(payload.ownerId || "") === vendorOwnerRef.current);
        if (mine) dispatch(fetchVendorDetails(vendorId));
      },
    },
    { enabled: Boolean(vendorId) },
  );

  // Rolled-up "store rating" from the polymorphic Review collection: combines
  // reviews written directly against this vendor with every review across its
  // products, so the banner reflects the ratings shoppers actually leave (which
  // land on products, not the bare vendor entity). Single source of truth is
  // the backend GET /api/reviews/vendor-summary.
  useEffect(() => {
    if (!vendorId) return;
    let active = true;
    getVendorRatingSummary({
      subjectType: "vendor_retailer",
      subjectId: vendorId,
    })
      .then((res) => {
        const stats = res?.data?.data?.stats;
        if (active && stats) {
          setVendorRating({
            avg: Number(stats.averageRating) || 0,
            count: Number(stats.totalReviews) || 0,
          });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [vendorId]);

  // Canonicalize BOTH halves of the URL param once the vendor doc loads.
  // The backend lookup accepts `{$or:[{_id},{owner}]}`, so this page also
  // renders for owner-id (Users) URLs — but the MULTI-VENDOR cart keys
  // `carts[vendorId]`, and an owner-id key would split this vendor's items
  // into a second cart group (double checkout/delivery fee). If we arrived
  // via the alias id: merge any alias cart into the canonical store-id cart,
  // re-point currentVendor, then history.replace to the canonical URL so the
  // cart selector, coverage and reviews all re-key to the entity _id.
  useEffect(() => {
    if (!vendor?.name || !vendor?._id) return;
    const canonicalId = String(vendor._id);
    const ownerId = String(vendor.owner?._id ?? vendor.owner ?? "");
    // Guard against a STALE vendor doc from a previously visited shop while
    // this page's fetch is still in flight — only act when the loaded vendor
    // is genuinely the one this URL points at.
    const matchesThisPage =
      canonicalId === String(vendorId) ||
      (ownerId && ownerId === String(vendorId));
    if (!matchesThisPage) return;
    if (canonicalId !== String(vendorId)) {
      dispatch(mergeVendorCarts({ fromId: vendorId, toId: canonicalId }));
      dispatch(setCurrentVendor(canonicalId));
    }
    const canonical = buildVendorParam(vendor.name, canonicalId);
    if (vendorParam !== canonical) {
      history.replace(`/shop/${canonical}`);
    }
  }, [
    vendor?.name,
    vendor?._id,
    vendor?.owner,
    vendorId,
    vendorParam,
    history,
    dispatch,
  ]);

  useEffect(() => {
    if (vendor && vendor.sections && vendor.sections.length > 0) {
      const firstSectionWithProducts = vendor.sections.find(
        (section) => section.products && section.products.length > 0,
      );
      if (firstSectionWithProducts) {
        setActiveSection(`section-${firstSectionWithProducts._id}`);
        setTimeout(() => {
          const sectionElement = document.getElementById(
            `section-${firstSectionWithProducts._id}`,
          );
          if (sectionElement) {
            // Measured bars, not a guess; see utils/stickyStack.
            scrollToSection(
              sectionElement,
              document.querySelector("[data-sections-container]"),
              { pinStack: false }
            );
          }
        }, 100);
      }
    }
  }, [vendor]);

  // Scroll-spy: track which section is active based on SectionsContainer scroll position
  useEffect(() => {
    const container = document.querySelector("[data-sections-container]");
    if (!container || !vendor?.sections) return;

    const sectionsWithProducts = vendor.sections.filter(
      (section) =>
        section.products &&
        section.products.filter((product) => product.enabled).length > 0,
    );

    if (sectionsWithProducts.length === 0) return;

    const handleScroll = () => {
      const containerTop = container.getBoundingClientRect().top;
      // Trigger line at 35% down from the container's visible top edge
      const triggerLine = containerTop + container.clientHeight * 0.35;

      let currentActiveId = null;

      for (const section of sectionsWithProducts) {
        const element = document.getElementById(`section-${section._id}`);
        if (!element) continue;
        if (element.getBoundingClientRect().top <= triggerLine) {
          currentActiveId = `section-${section._id}`;
        }
      }

      // Default to the first section if nothing has scrolled past yet
      if (!currentActiveId && sectionsWithProducts.length > 0) {
        currentActiveId = `section-${sectionsWithProducts[0]._id}`;
      }

      if (currentActiveId) {
        setActiveSection(currentActiveId);
      }
    };

    // Run once immediately to set initial state
    handleScroll();

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [vendor]);

  // Auto-scroll the active section tab into view in the horizontal nav
  useEffect(() => {
    if (activeSection && sectionsNavRef.current) {
      const activeTab = sectionsNavRef.current.querySelector(
        `a[href="#${activeSection}"]`,
      );
      if (activeTab) {
        activeTab.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [activeSection]);

  const handleSectionClick = (sectionId) => (event) => {
    event.preventDefault();
    const sectionElement = document.getElementById(sectionId);
    const container =
      sectionElement?.closest('[style*="overflow"]') ||
      document.querySelector("[data-sections-container]");

    if (sectionElement && container) {
      scrollToSection(sectionElement, container);
      setActiveSection(sectionId);
    } else if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(sectionId);
    }
  };

  const getLinkClasses = (sectionId) => {
    return activeSection === sectionId ? "active-section" : "";
  };

  const handleProductClick = (product) => {
    // Hard block (with a warning) when the vendor can't deliver to the
    // customer's set location — don't let the cart fill with undeliverable
    // items. Undecidable (no location / vendor coords) never blocks.
    if (coverage.isOutOfRange) {
      toast.warn(
        "This vendor doesn't deliver to your set location. Change your location or pick a vendor that delivers to you.",
      );
      return;
    }
    setSelectedProduct(product);
  };

  /* The product's own page. The expand button opens it, and the card's
     picture and name link to it so search engines can reach it. */
  const productPagePath = (product) =>
    productPathUnder(`/shop/${buildVendorParam(vendor?.name, vendorId)}`, product);

  /* Navigate to the dedicated product page (larger images, zoom,
     related products). Card click keeps opening the quick-view modal;
     this is the quiet expand affordance next to share. */
  const handleViewProductPage = (e, product) => {
    e.stopPropagation();
    history.push({
      pathname: productPagePath(product),
      state: { product },
    });
  };

  const handleProductShare = (e, product) => {
    e.stopPropagation();
    // Open the visual share modal (branded poster preview + share actions)
    // instead of dumping plain text into the OS share sheet. WhatsApp Status
    // and chats are visual-first, so a poster converts far better than a link.
    const slugParam = buildVendorParam(vendor?.name, vendorId);
    const shopUrl = withVendorCacheBuster(
      `${SITE_ORIGIN}/shop/${slugParam}`,
      vendor,
    );
    setShareProductUrl(shopUrl);
    setShareProduct({ ...product, vendorName: vendor?.name });
  };

  const handleProductWhatsAppShare = (e, product) => {
    e.stopPropagation();
    const slugParam = buildVendorParam(vendor?.name, vendorId);
    const shopUrl = withVendorCacheBuster(
      `${SITE_ORIGIN}/shop/${slugParam}`,
      vendor,
    );
    const text = buildProductShareText(product, { url: shopUrl, vendor });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  const handleShareClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseShareMenu = () => {
    setAnchorEl(null);
  };

  /* Featured product list shown in the share preview — kept to 5 enabled
     items so WhatsApp/clipboard payloads stay readable. Both copy-link
     and WhatsApp share use the same list and the same builder, so the
     two surfaces stay in sync. */
  const buildShopShareText = () => {
    const slugParam = buildVendorParam(vendor?.name, vendorId);
    const url = withVendorCacheBuster(
      `${process.env.REACT_APP_CLIENT_URL}/shop/${slugParam}`,
      vendor,
    );
    const products = (vendor?.sections || [])
      .flatMap((s) => s.products?.filter((p) => p.enabled) || [])
      .slice(0, 5);
    return buildVendorShareText(vendor, { url, products, headerLabel: "Shop" });
  };

  const handleCopyLink = () => {
    if (!vendor) return;
    navigator.clipboard
      .writeText(buildShopShareText())
      .then(() => toast.success("Shop details copied to clipboard!"))
      .catch((err) => {
        console.error("Failed to copy: ", err);
        toast.error("Failed to copy to clipboard");
      });
    setAnchorEl(null);
  };

  const handleWhatsAppShare = () => {
    if (!vendor) return;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(buildShopShareText())}`,
      "_blank",
    );
    setAnchorEl(null);
  };

  return (
    <Container>
      {/* Vendor-first social card: passes vendor.bannerUrl as the OG image,
          vendor.name as the title, and emits an Organization JSON-LD with
          the vendor as the org. Falls back to Lusuku defaults if the vendor
          has no branding uploaded. */}
      <SEOHead
        {...buildVendorSeoProps(vendor, {
          /* The named address once the shop is loaded; until then, and if
             the read fails, the address the page was opened at (the named
             one every link and the sitemap use), never the bare id. */
          canonicalPath: shopPath("retail", vendor) || location.pathname,
          vendorType: "retailer",
        })}
      />
      {/* Subtle navbar tint signals the user is now inside a
          retailer/wholesaler shop (vs. the default marketplace teal). */}
      <Navbar bgColor="#2d3e50" />
      {status ? (
        <SkeletonContainer>
          {/* Banner Skeleton */}
          <SkeletonBanner>
            <SkeletonAvatarBox />
            <SkeletonBannerText>
              <SkeletonLine width="60%" height="24px" />
              <SkeletonLine
                width="40%"
                height="14px"
                style={{ marginTop: "10px" }}
              />
              <SkeletonLine
                width="25%"
                height="12px"
                style={{ marginTop: "8px" }}
              />
            </SkeletonBannerText>
          </SkeletonBanner>

          {/* Section Nav Skeleton */}
          <SkeletonNav>
            {[1, 2, 3, 4].map((i) => (
              <SkeletonNavItem key={i} width={`${60 + Math.random() * 40}px`} />
            ))}
          </SkeletonNav>

          {/* Section Title Skeleton */}
          <SkeletonSectionTitle>
            <SkeletonLine width="180px" height="20px" />
          </SkeletonSectionTitle>

          {/* Product Grid Skeleton */}
          <SkeletonGrid>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <SkeletonCard key={i}>
                <SkeletonImage />
                <SkeletonCardBody>
                  <SkeletonLine width="80%" height="14px" />
                  <SkeletonLine
                    width="100%"
                    height="10px"
                    style={{ marginTop: "8px" }}
                  />
                  <SkeletonLine
                    width="60%"
                    height="10px"
                    style={{ marginTop: "4px" }}
                  />
                  <SkeletonPriceRow>
                    <SkeletonLine width="70px" height="14px" />
                    <SkeletonCircle />
                  </SkeletonPriceRow>
                </SkeletonCardBody>
              </SkeletonCard>
            ))}
          </SkeletonGrid>
        </SkeletonContainer>
      ) : error ? (
        <ErrorIndicator>
          Error: {typeof error === "object" ? error?.message : error}
        </ErrorIndicator>
      ) : (
        vendor && (
          <>
            <VendorInfo>
              <VendorBannerImage
                src={
                  vendor?.shopBannerUrl ||
                  vendor?.bannerUrl ||
                  vendorBackgroundBanner
                }
                alt="Vendor Banner"
              />
              <VendorInfoOverlay>
                <AvatarWrapper>
                  {vendor?.imageUrl ? (
                    <StyledAvatar alt={vendor?.name} src={vendor.imageUrl} />
                  ) : (
                    <AvatarPlaceholder>
                      <Storefront />
                    </AvatarPlaceholder>
                  )}
                </AvatarWrapper>
                <VendorText>
                  <h1>{vendor.name}</h1>
                  <HeaderTagline vendor={vendor} />
                  <p>{vendor.address}</p>
                  {vendorRating && vendorRating.count > 0 ? (
                    <p
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <Stars value={vendorRating.avg} size={16} />
                      <span style={{ fontSize: 13 }}>
                        {vendorRating.avg.toFixed(1)} ({vendorRating.count}{" "}
                        review
                        {vendorRating.count !== 1 ? "s" : ""})
                      </span>
                    </p>
                  ) : (
                    <p style={{ fontSize: 13, opacity: 0.85 }}>
                      No ratings yet
                    </p>
                  )}
                </VendorText>
                <ShareButton onClick={handleShareClick}>
                  <ShareIcon />
                </ShareButton>
              </VendorInfoOverlay>
              <StyledMenu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseShareMenu}
              >
                <MenuItem onClick={handleCopyLink}>
                  <ListItemIcon>
                    <LinkIcon fontSize="small" style={{ color: "#1976d2" }} />
                  </ListItemIcon>
                  <ListItemText primary="Copy Shop Link" />
                </MenuItem>
                <MenuItem onClick={handleWhatsAppShare}>
                  <ListItemIcon>
                    <WhatsAppIcon
                      fontSize="small"
                      style={{ color: "#25D366" }}
                    />
                  </ListItemIcon>
                  <ListItemText primary="Share on WhatsApp" />
                </MenuItem>
              </StyledMenu>
            </VendorInfo>
            <DesktopCoverageBannerWrap>
              <RestaurantAvailabilityBanner availability={availability} />
              <CoverageBanner
                variant="block"
                deliverable={coverage.deliverable}
                reason={coverage.reason}
                distanceKm={coverage.distanceKm}
                radius={coverage.radius}
                hasLocation={coverage.hasLocation}
                status={coverage.status}
                error={coverage.error}
                onRetry={coverage.refresh}
                onChangeLocation={() => setShowLocationPrompt(true)}
              />
            </DesktopCoverageBannerWrap>
            {vendor.isActive ? (
              vendor.sections &&
              vendor.sections.filter(
                (section) =>
                  section.products &&
                  section.products.filter((product) => product.enabled).length >
                    0,
              ).length > 0 ? (
                <>
                  <SectionsNav ref={sectionsNavRef}>
                    {vendor.sections
                      .filter(
                        (section) =>
                          section.products &&
                          section.products.filter((product) => product.enabled)
                            .length > 0,
                      )
                      .map((section) => (
                        <SectionLink
                          key={section._id}
                          href={`#section-${section._id}`}
                          onClick={handleSectionClick(`section-${section._id}`)}
                          className={getLinkClasses(`section-${section._id}`)}
                        >
                          {section.title}
                        </SectionLink>
                      ))}
                  </SectionsNav>
                  <MobileCoverageBannerWrap>
                    <CoverageBanner
                      variant="block"
                      deliverable={coverage.deliverable}
                      reason={coverage.reason}
                      distanceKm={coverage.distanceKm}
                      radius={coverage.radius}
                      hasLocation={coverage.hasLocation}
                      status={coverage.status}
                      error={coverage.error}
                      onRetry={coverage.refresh}
                      onChangeLocation={() => setShowLocationPrompt(true)}
                    />
                  </MobileCoverageBannerWrap>
                  <SectionsContainer data-sections-container>
                    {vendor.sections
                      .filter(
                        (section) =>
                          section.products &&
                          section.products.filter((product) => product.enabled)
                            .length > 0,
                      )
                      .map((section) => (
                        <Section
                          key={section._id}
                          id={`section-${section._id}`}
                        >
                          <SectionHeader>
                            <h2>{section.title}</h2>
                            <p className="section-description">
                              || {section.description}
                            </p>
                          </SectionHeader>
                          <ProductsGrid>
                            {section.products
                              .filter((product) => product.enabled)
                              .map((product) => {
                                const isInCart = Boolean(cart[product._id]);
                                const isImageLoaded = loadedImages[product._id];
                                const onOffer = hasSalePrice(product);
                                return (
                                  <ProductCard
                                    key={product._id}
                                    onClick={() => handleProductClick(product)}
                                  >
                                    <ProductImageWrapper>
                                      {!isImageLoaded && (
                                        <ProductImageSkeleton />
                                      )}
                                      {onOffer && (
                                        <DealTag
                                          discount={product.discount}
                                          size="m"
                                          radius={12}
                                        />
                                      )}
                                      {/* A plain tap still opens the
                                          quick view; the href is for
                                          crawlers and new tabs. */}
                                      <CrawlLink
                                        to={productPagePath(product)}
                                        onNavigate={() =>
                                          handleProductClick(product)
                                        }
                                        style={{
                                          display: "block",
                                          width: "100%",
                                          height: "100%",
                                        }}
                                      >
                                        <ProductImg
                                          src={product.image}
                                          alt={product.name}
                                          isLoaded={isImageLoaded}
                                          onLoad={() =>
                                            handleImageLoaded(product._id)
                                          }
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                            setLoadedImages((prev) => ({
                                              ...prev,
                                              [product._id]: "fallback",
                                            }));
                                          }}
                                        />
                                      </CrawlLink>
                                      {isImageLoaded === "fallback" && (
                                        <FallbackImageContainer>
                                          <FallbackIcon>📦</FallbackIcon>
                                        </FallbackImageContainer>
                                      )}
                                      {coverage.isOutOfRange && (
                                        <OutOfRangeBadge>
                                          Outside delivery area
                                        </OutOfRangeBadge>
                                      )}
                                    </ProductImageWrapper>
                                    <ProductDetails>
                                      <ProductName>
                                        <CrawlLink
                                          to={productPagePath(product)}
                                          onNavigate={() =>
                                            handleProductClick(product)
                                          }
                                        >
                                          {product.name}
                                        </CrawlLink>
                                      </ProductName>
                                      {product.description && (
                                        <ProductDescription>
                                          {product.description}
                                        </ProductDescription>
                                      )}
                                      <ProductFooter>
                                        <PriceUnitGroup>
                                          <PriceCut
                                            component={ProductPrice}
                                            price={product.price}
                                            listPrice={product.listPrice}
                                            salePrice={product.salePrice}
                                            format={formatCurrency}
                                          />
                                          {product.unit_name && (
                                            <ProductUnits>
                                              {product.unit_name}
                                            </ProductUnits>
                                          )}
                                        </PriceUnitGroup>
                                        <ProductCardActions>
                                          <Tooltip title="View full details">
                                            <ProductShareButton
                                              onClick={(e) =>
                                                handleViewProductPage(
                                                  e,
                                                  product,
                                                )
                                              }
                                              aria-label="View full details"
                                            >
                                              <OpenInFullIcon
                                                style={{ fontSize: 15 }}
                                              />
                                            </ProductShareButton>
                                          </Tooltip>
                                          <Tooltip title="Share product">
                                            <ProductShareButton
                                              onClick={(e) =>
                                                handleProductShare(e, product)
                                              }
                                              onContextMenu={(e) => {
                                                e.preventDefault();
                                                handleProductWhatsAppShare(
                                                  e,
                                                  product,
                                                );
                                              }}
                                              title="Click to copy · Right-click for WhatsApp"
                                            >
                                              <ShareIcon
                                                style={{ fontSize: 16 }}
                                              />
                                            </ProductShareButton>
                                          </Tooltip>
                                          <Tooltip
                                            title={
                                              coverage.isOutOfRange
                                                ? "Outside delivery area — change your location to order"
                                                : isInCart
                                                  ? "Item in cart"
                                                  : "Add to Cart"
                                            }
                                          >
                                            <AddToCartContainer
                                              className={
                                                isInCart ? "in-cart" : "add"
                                              }
                                              style={{
                                                opacity: coverage.isOutOfRange
                                                  ? 0.4
                                                  : 1,
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleProductClick(product);
                                              }}
                                            >
                                              {isInCart ? (
                                                <InCartIcon />
                                              ) : (
                                                <AddToCartIcon />
                                              )}
                                            </AddToCartContainer>
                                          </Tooltip>
                                        </ProductCardActions>
                                      </ProductFooter>
                                    </ProductDetails>
                                  </ProductCard>
                                );
                              })}
                          </ProductsGrid>
                        </Section>
                      ))}
                  </SectionsContainer>
                </>
              ) : (
                <NoProductsMessage>
                  No Active Products at the moment...
                </NoProductsMessage>
              )
            ) : (
              <InactiveMessage>
                This vendor is currently inactive and not accepting orders.
              </InactiveMessage>
            )}
          </>
        )
      )}
      <Tooltip title="View Cart">
        <FloatingCartIcon onClick={() => history.push("/cart")}>
          <Badge badgeContent={cartCount} color="secondary">
            🛒
          </Badge>
        </FloatingCartIcon>
      </Tooltip>
      {isCartOpen && (
        <CartOverlay>
          <RetailerWholesalerCart onClose={() => setIsCartOpen(false)} />
        </CartOverlay>
      )}
      {selectedProduct && (
        <RetailerWholesalerProductModal
          open={Boolean(selectedProduct)}
          onClose={handleCloseModal}
          product={selectedProduct}
        />
      )}
      {showLocationPrompt && (
        <LocationPrompt open onClose={() => setShowLocationPrompt(false)} />
      )}

      {/* Vendor-level reviews (retailer / wholesaler). */}
      {vendorId && (
        <Reviews
          subjectType="vendor_retailer"
          subjectId={vendorId}
          title={`Reviews for ${vendor?.name || "this vendor"}`}
          reserveFloatingActionSpace
        />
      )}
      <VendorWhatsAppFloat
        phone={vendor?.phone}
        vendorName={vendor?.name}
        vendorType="retail"
        pageUrl={`${SITE_ORIGIN}/shop/${buildVendorParam(vendor?.name, vendorId)}`}
      />

      {/* Visual share poster modal (branded poster preview + share actions). */}
      <ProductShareModal
        visible={Boolean(shareProduct)}
        item={shareProduct}
        shopUrl={shareProductUrl}
        onClose={() => setShareProduct(null)}
      />
    </Container>
  );
};

export default VendorShopPage;

const Container = styled.div`
  padding: 20px;
  /* Offset for the fixed standard Navbar.
     Desktop: 95px logo + 10px marginBottom + 16px wrapper padding ≈ 121px.
     Mobile (≤850px): logo/auth row + search row ≈ 130px. */
  padding-top: var(--lusuku-nav-h, 121px);

  @media (max-width: 850px) {
    padding: var(--lusuku-nav-h, 132px) 10px 20px;
  }
`;

const VendorInfo = styled.div`
  margin-bottom: 20px;
  position: relative;
  width: 100%;
  max-width: 1440px;
  aspect-ratio: 1440 / 175;
  margin-left: auto;
  margin-right: auto;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    height: 200px;
    aspect-ratio: auto;
    border-radius: 12px;
  }
`;

const VendorBannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VendorInfoOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.55) 22%,
    rgba(0, 0, 0, 0.12) 48%,
    transparent 72%
  );
  /* Sit the info block low, inside the readable dark band. */
  padding: 22px 20px 12px;
  display: flex;
  align-items: flex-end;
  gap: 15px;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const AvatarWrapper = styled.div`
  width: 90px;
  height: 90px;
  flex-shrink: 0;
  border-radius: 10px;
  overflow: hidden;
  background-color: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
  }
`;

const StyledAvatar = styled(Avatar)`
  width: 100% !important;
  height: 100% !important;
  border-radius: 8px !important;
  object-fit: contain;
`;

/* Polished fallback used when the vendor has no logo (imageUrl).
   Mirrors the placeholder treatment used on AllVendorsPage so the
   look stays consistent across the site. */
const AvatarPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(
      circle at 28% 24%,
      rgba(255, 255, 255, 0.55) 0%,
      rgba(255, 255, 255, 0.1) 32%,
      transparent 58%
    ),
    linear-gradient(145deg, #ffb54b 0%, #f7941d 48%, #de6e05 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -2px 6px rgba(0, 0, 0, 0.12);

  svg {
    color: #fff;
    font-size: 42px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25));
  }

  @media (max-width: 768px) {
    svg {
      font-size: 34px;
    }
  }
`;

/* The vendor's own tagline under the name, in the header's white type.
   Nothing renders when the vendor has not written one
   (components/VendorTagline.jsx). && outranks the header's own p rule. */
const HeaderTagline = styled(VendorTagline)`
  && {
    margin: 4px 0 6px;
    max-width: 560px;
    font-size: 0.95rem;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.92);
  }
`;

const VendorText = styled.div`
  flex: 1;
  color: white;

  h1 {
    font-size: 1.5em;
    margin: 0 0 4px 0;
    font-weight: 600;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);

    @media (max-width: 768px) {
      font-size: 1.2em;
    }
  }

  p {
    margin: 2px 0;
    font-size: 0.9em;
    opacity: 0.95;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);

    @media (max-width: 768px) {
      font-size: 0.8em;
    }
  }
`;

const ShareButton = styled(IconButton)`
  color: white !important;
  background-color: rgba(255, 255, 255, 0.2) !important;
  flex-shrink: 0;
  transition: all 0.3s ease !important;

  &:hover {
    background-color: rgba(255, 255, 255, 0.3) !important;
    transform: scale(1.1);
  }
`;

const StyledMenu = styled(Menu)`
  .MuiMenuItem-root {
    padding: 12px 24px;

    &:hover {
      background-color: #f5f5f5;

      .MuiListItemIcon-root {
        transform: scale(1.1);
      }
    }
  }

  .MuiListItemIcon-root {
    transition: transform 0.2s ease;
  }
`;

const DesktopCoverageBannerWrap = styled.div`
  padding: 0 16px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileCoverageBannerWrap = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    padding: 8px 0 10px;
    background: #ffffff;
  }
`;

const SectionsNav = styled.nav`
  /* Pinned under the Navbar at the height it actually rendered; a fixed 121px
     put the strip over the search field on a phone. See utils/stickyStack. */
  ${stickyUnderNav}
  background-color: white;
  z-index: 1000;
  padding: 10px 0;
  display: flex;
  justify-content: space-around;
  border-bottom: 1px solid #ccc;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3); /* Add shadow to section nav */
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;

  @media (max-width: 768px) {
    overflow-x: auto; /* Enable horizontal scrolling for smaller screens */
    white-space: nowrap; /* Prevent wrapping of section links */
    margin-bottom: 0;
    -webkit-overflow-scrolling: touch; /* Enable smooth scrolling on iOS */
    scrollbar-width: none; /* Hide scrollbar for Firefox */
    &::-webkit-scrollbar {
      display: none; /* Hide scrollbar for WebKit browsers */
    }
  }
`;

const SectionLink = styled.a`
  text-decoration: none;
  color: black;
  padding: 10px 20px;
  font-weight: normal;
  transition: all 0.3s ease;

  &.active-section {
    color: #f24405;
    font-weight: bold;
    border-bottom: 2px solid #84b026;
  }

  &:hover {
    background-color: #f0f0f0;
  }

  @media (max-width: 768px) {
    padding: 5px 10px;
    display: inline-block; /* Ensure links are displayed inline */
  }
`;

const SectionsContainer = styled.div`
  position: relative;
  margin-top: 2px;
  overflow-y: auto;
  /* Exactly what the two bars leave of the viewport; 160 and 270 were
     guesses at the same sum. */
  ${heightUnderStack(2)}
  padding-bottom: 80px;
  scroll-behavior: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding-bottom: 100px;
  }
`;

const Section = styled.section`
  margin-bottom: 8px;
  padding-top: 22px;
  ${sectionScrollMargin}

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 18px 20px;
  background-color: #ffffff;
  border-left: 5px solid #f24405;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
  position: relative;
  gap: 16px;

  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: #84b026;
    border-radius: 8px 0 0 8px;
    transform: scaleY(0);
    transform-origin: bottom;
    transition: transform 0.3s ease;
  }

  &:hover:before {
    transform: scaleY(1);
  }

  h2 {
    margin: 0;
    font-size: 1.6em;
    font-weight: 700;
    color: #1a1a1a;
    position: relative;
    white-space: nowrap;
    padding-right: 20px;

    &:after {
      content: "";
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 8px;
      height: 8px;
      background-color: #f24405;
      border-radius: 50%;
      box-shadow: 0 0 0 3px rgba(242, 68, 5, 0.2);
    }

    @media (max-width: 768px) {
      font-size: 1.3em;
      padding-right: 16px;
    }
  }

  .section-description {
    margin: 0;
    font-size: 0.9em;
    font-style: italic;
    color: #666;
    font-weight: 400;
    line-height: 1.4;
    flex: 1;
    padding-left: 16px;
    border-left: 2px solid #f0f0f0;
    position: relative;

    &:before {
      content: "";
      position: absolute;
      left: -6px;
      top: 50%;
      transform: translateY(-50%);
      width: 10px;
      height: 10px;
      background-color: #84b026;
      border-radius: 50%;
      border: 2px solid white;
    }

    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr); /* Adjust for mobile */
    gap: 14px;
  }
`;

const ProductCard = styled.div`
  border: 1px solid #eaeaea;
  text-align: left;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  background-color: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: #84b026;
  }
`;

/* ProductImage replaced by progressive loading components below */

const ProductDetails = styled.div`
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;

  @media (max-width: 768px) {
    padding: 10px 12px;
    gap: 4px;
  }
`;

const ProductName = styled.h5`
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ProductPrice = styled.span`
  font-size: 15px;
  color: #2d8a2d;
  font-weight: 700;
  letter-spacing: 0;
  /* nowrap keeps each figure whole; the flex wrap below is what allows a
     break between the struck price and the sale price. */
  white-space: nowrap;
  ${cardPriceRow}

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const ProductDescription = styled.p`
  font-size: 12.5px;
  color: #888;
  margin: 0;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (max-width: 768px) {
    font-size: 11.5px;
    -webkit-line-clamp: 1;
  }
`;

const ProductFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  padding-top: 8px;
  ${cardFooterRow}
`;

const PriceUnitGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
  flex: 1 1 auto;
  ${cardPriceOwnsItsRow}

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const ProductCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  ${cardActionsRow}

  @media (max-width: 768px) {
    gap: 5px;
  }

  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const ProductShareButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid #e0e0e0;
  background: #f8f8f8;
  color: #555;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  padding: 0;

  &:hover {
    background: #eaf4d3;
    border-color: #84b026;
    color: #5a8a00;
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    border-width: 1px;
  }

  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
  }
`;

const AddToCartContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  transition:
    background 0.18s ease,
    transform 0.15s ease;

  &.add {
    background: #f24405;
    box-shadow: 0 3px 10px rgba(242, 68, 5, 0.4);
    &:hover {
      background: #d43a04;
      transform: scale(1.08);
    }
    &:active {
      transform: scale(0.96);
    }
  }

  &.in-cart {
    background: #84b026;
    box-shadow: 0 3px 10px rgba(132, 176, 38, 0.4);
    &:hover {
      background: #6d9420;
      transform: scale(1.08);
    }
    &:active {
      transform: scale(0.96);
    }
  }

  @media (max-width: 768px) {
    width: 34px;
    height: 34px;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }
`;

const AddToCartIcon = styled(AddShoppingCart)`
  font-size: 1.5rem !important;
  color: #fff;
  display: block;

  @media (max-width: 768px) {
    font-size: 1.12rem !important;
  }

  @media (max-width: 480px) {
    font-size: 1rem !important;
  }
`;

const InCartIcon = styled(CheckCircle)`
  font-size: 1.5rem !important;
  color: #fff;
  display: block;

  @media (max-width: 768px) {
    font-size: 1.12rem !important;
  }

  @media (max-width: 480px) {
    font-size: 1rem !important;
  }
`;

const ProductUnits = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #666;
  padding: 3px 10px;
  border-radius: 12px;
  background-color: #f5f5f5;
  border: 1px solid #e8e8e8;
  white-space: nowrap;
  letter-spacing: 0.2px;

  @media (max-width: 768px) {
    font-size: 10px;
    padding: 2px 8px;
  }
`;

const FloatingCartIcon = styled.div`
  position: fixed;
  bottom: 90px;
  right: 20px;
  font-size: 2rem;
  background-color: #00ff00;
  color: white;
  padding: 15px; /* Increase padding */
  border-radius: 75%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: background-color 0.2s; /* Add a transition for smooth color change on hover */

  &:hover {
    background-color: #02ff88;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem; /* Adjust size for mobile */
    padding: 12px;
    bottom: 76px;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem; /* Adjust size for small mobile screens */
    padding: 10px;
    bottom: 76px;
    right: 10px;
  }
`;

const CartOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

/* ======================== */
/* Shimmer Skeleton Styles  */
/* ======================== */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const SkeletonPulse = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
  border-radius: 8px;
`;

const SkeletonContainer = styled.div`
  padding-top: 10px;
  animation: ${fadeIn} 0.3s ease;
`;

const SkeletonBanner = styled(SkeletonPulse)`
  width: 100%;
  height: 120px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 20px 24px;
  box-sizing: border-box;
  background: linear-gradient(90deg, #e8e8e8 25%, #d8d8d8 37%, #e8e8e8 63%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;

  @media (max-width: 768px) {
    height: 100px;
    padding: 16px 20px;
  }
`;

const SkeletonAvatarBox = styled(SkeletonPulse)`
  width: 120px;
  height: 90px;
  min-width: 120px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.15) 25%,
    rgba(255, 255, 255, 0.25) 37%,
    rgba(255, 255, 255, 0.15) 63%
  );
  background-size: 800px 100%;
`;

const SkeletonBannerText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const SkeletonLine = styled(SkeletonPulse)`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "14px"};
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.08) 25%,
    rgba(255, 255, 255, 0.18) 37%,
    rgba(255, 255, 255, 0.08) 63%
  );
  background-size: 800px 100%;
`;

const SkeletonNav = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 0;
  margin-top: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const SkeletonNavItem = styled(SkeletonPulse)`
  width: ${(props) => props.width || "80px"};
  height: 34px;
  border-radius: 8px;
`;

const SkeletonSectionTitle = styled.div`
  margin-top: 20px;
  margin-bottom: 16px;
  padding: 14px 20px;
  border-left: 5px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }
`;

const SkeletonCard = styled.div`
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

const SkeletonImage = styled(SkeletonPulse)`
  width: 100%;
  height: 210px;
  border-radius: 4px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    height: 150px;
  }
`;

const SkeletonCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SkeletonPriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
`;

const SkeletonCircle = styled(SkeletonPulse)`
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
`;

/* ================================ */
/* Progressive Image Loading Styles */
/* ================================ */

const OutOfRangeBadge = styled.div`
  position: absolute;
  top: 8px;
  /* Right-aligned, as on the restaurant menu: the deal stub owns the
     top-left corner. */
  right: 8px;
  background: rgba(138, 43, 6, 0.92);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 4px 8px;
  border-radius: 6px;
  z-index: 2;
  pointer-events: none;
`;

const ProductImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 220px;
  overflow: hidden;
  background-color: #ffffff;
  border-bottom: 1px solid #eee;
  margin-bottom: 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    height: 170px;
    padding: 8px;
  }
`;

const productImageShimmer = keyframes`
  0% { background-position: -300px 0; }
  100% { background-position: 300px 0; }
`;

const ProductImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f5f5f5 25%, #ebebeb 37%, #f5f5f5 63%);
  background-size: 600px 100%;
  animation: ${productImageShimmer} 1.4s ease-in-out infinite;
`;

const ProductImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  opacity: ${(props) => (props.isLoaded === true ? 1 : 0)};
  transition: opacity 0.4s ease;
`;

const FallbackImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const FallbackIcon = styled.span`
  font-size: 48px;
  opacity: 0.3;
  filter: grayscale(0.5);

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const ErrorIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 24px;
  color: red;

  @media (max-width: 768px) {
    font-size: 20px; /* Adjust font size for mobile */
  }

  @media (max-width: 480px) {
    font-size: 16px; /* Adjust font size for small mobile screens */
  }
`;

const InactiveMessage = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 50vh; /* Adjust as needed */
  font-size: 22px;
  font-style: italic;
  color: red;
  text-align: center;
  background-color: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    font-size: 20px; /* Adjust font size for mobile */
    padding: 15px;
  }

  @media (max-width: 480px) {
    font-size: 16px; /* Adjust font size for small mobile screens */
    padding: 10px;
  }

  h1 {
    font-size: 1.5em;
    margin-bottom: 10px;
  }

  p {
    font-size: 1em;
    margin: 0;
    color: #721c24;
  }
`;

const NoProductsMessage = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 50vh;
  font-size: 18px;
  font-style: italic;
  color: #721c24;
  text-align: center;
  background-color: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  margin: 20px;
  transition:
    transform 0.3s,
    box-shadow 0.3s;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 20px; /* Adjust font size for mobile */
    padding: 15px;
  }

  @media (max-width: 480px) {
    font-size: 16px; /* Adjust font size for small mobile screens */
    padding: 10px;
  }

  h1 {
    font-size: 1.8em;
    margin-bottom: 10px;
    color: #721c24;
  }

  p {
    font-size: 1.2em;
    margin: 0;
    color: #721c24;
  }

  svg {
    font-size: 3rem;
    color: #721c24;
    margin-bottom: 15px;
  }
`;
